import PropTypes from "prop-types";
import { memo } from "react";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { htmlSanitize } from "app/utils/sanitize";
import Collapse from "app/components/ui/collapse/Collapse";
import { ButtonLink } from "app/components/ui/ButtonLink/ButtonLink";
import { ArrowDownIcon, ArrowRightIcon } from "app/icons/navigation";
import { stripWrappingTag } from "app/utils/html/html";

const BlockSeo = ({ blockTextTitle, blockTextDescriptions, blockLinkTitle, blockLinks }) => {
	return (
		<div className="block-seo" data-testid="block-seo">
			<div className="block-seo__container">
				<div className="block-seo__section">
					{blockTextTitle && (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.XL4}
							isBold
							className="block-seo__title"
							component="h2"
							data-testid="block-seo-title"
							dangerouslySetInnerHTML={{
								__html: htmlSanitize(stripWrappingTag(blockTextTitle)),
							}}
						/>
					)}
					{Array.isArray(blockTextDescriptions) && blockTextDescriptions.length > 0 && (
						<Collapse
							accordion
							defaultActiveKey={["0"]}
							className="block-seo__description-collapse"
							expandIcon={({ isActive }) => (
								<ArrowDownIcon
									style={{
										transition: "transform .2s ease",
										transform: `rotate(${isActive ? 180 : 0}deg)`,
									}}
									className="icon"
								/>
							)}
						>
							{blockTextDescriptions.map((item, index) => (
								<Collapse.Panel
									key={item.title || index}
									headerClass="block-seo__collapse-header"
									header={
										<Typography
											variant={TYPOGRAPHY_VARIANTS.REGULAR}
											isBold
											component="h3"
											data-testid="block-seo-description-title"
										>
											<span
												dangerouslySetInnerHTML={{
													__html: htmlSanitize(item.title),
												}}
											/>
										</Typography>
									}
								>
									<Typography
										variant={TYPOGRAPHY_VARIANTS.REGULAR}
										component="p"
										className="block-seo__description-content"
									>
										<span
											dangerouslySetInnerHTML={{
												__html: htmlSanitize(item.description),
											}}
										/>
									</Typography>
								</Collapse.Panel>
							))}
						</Collapse>
					)}
				</div>
				{blockLinks?.length > 0 && (
					<div className="block-seo__section" data-testid="block-links">
						<Collapse
							className="block-seo__links-collapse"
							defaultActiveKey={["0"]}
							expandIcon={({ isActive }) => {
								return (
									<ArrowDownIcon
										style={{
											transition: "transform .2s ease",
											transform: `rotate(${isActive ? 180 : 0}deg)`,
										}}
										className="icon"
									/>
								);
							}}
						>
							<Collapse.Panel
								headerClass="block-seo__collapse-header"
								header={
									<Typography
										variant={TYPOGRAPHY_VARIANTS.XL4}
										isBold
										className="block-seo__title block-seo__links-title"
										component="div"
										data-testid="block-seo-links-title"
									>
										<span
											dangerouslySetInnerHTML={{
												__html: htmlSanitize(blockLinkTitle),
											}}
										/>
									</Typography>
								}
							>
								<div className="block-seo__links">
									{[0, 1, 2, 3].map(colIdx => (
										<div key={colIdx} className="block-seo__col">
											{blockLinks
												.filter((_, i) => i % 4 === colIdx)
												.map(link => (
													<ButtonLink
														variant="borderless"
														endIcon={<ArrowRightIcon />}
														key={link?.url}
														to={{ pathname: link?.url }}
														className="block-seo__link"
														data-testid="block-seo-link"
													>
														<Typography
															variant={TYPOGRAPHY_VARIANTS.REGULAR}
															component="span"
														>
															{link?.title}
														</Typography>
													</ButtonLink>
												))}
										</div>
									))}
								</div>
							</Collapse.Panel>
						</Collapse>
					</div>
				)}
			</div>
		</div>
	);
};

BlockSeo.propTypes = {
	blockTextTitle: PropTypes.string,
	blockTextDescriptions: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string,
			description: PropTypes.string,
		})
	),
	blockLinkTitle: PropTypes.string,
	blockLinks: PropTypes.array,
};

export default memo(BlockSeo);
